import validator from "validator";
import * as dates from "./dates";
import dayjs from "dayjs";

export function isNull(value: unknown) {
  return value === null || value === undefined || value === "";
}

export function isEmpty(value: unknown): boolean {
  if (isNull(value)) {
    return true;
  }
  if (typeof value === "string") {
    return value.trim().length === 0;
  }
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  if (typeof value === "object" && value !== null) {
    return Object.keys(value).length === 0;
  }
  if (typeof value === "number") {
    return value === 0;
  }
  return false;
}

export function checkLowercase(value: unknown) {
  if (typeof value === "string") {
    for (let i = 0; i < value.length; i++) {
      const char = value[i];
      if (char >= "a" && char <= "z") {
        return true; // Found a lowercase letter
      }
    }
  }
  return null; // No lowercase letters found
}

export function checkUppercase(value: unknown) {
  if (typeof value === "string") {
    for (let i = 0; i < value.length; i++) {
      const char = value[i];
      if (char >= "A" && char <= "Z") {
        return true; // Found a lowercase letter
      }
    }
  }
  return null; // No lowercase letters found
}

export function checkHasNumber(value: unknown) {
  if (typeof value === "string") {
    for (let i = 0; i < value.length; i++) {
      const char = value[i];
      if (!isNaN(parseInt(char))) {
        return true; // Found a lowercase letter
      }
    }
  }
  return null; // No lowercase letters found
}

export function checkNonAlphanumeric(value: unknown) {
  if (typeof value === "string") {
    return /[^a-zA-Z0-9]/.test(value);
  }
  return null; // No lowercase letters found
}

export function toNumbers(arr: string[]): number[] {
  return arr.map((s) => Number(s));
}

export function arrayEquals<T>(arr1?: T[], arr2?: T[]): boolean {
  const a1 = arr1 || [];
  const a2 = arr2 || [];
  return a1.length == a2.length && a1.every((n) => a2.includes(n));
}

export function isValidEmail(email: string): boolean {
  return !isEmpty(email) && validator.isEmail(email);
}

export type DataToValidate = unknown;
export type ValidationRule = (val: DataToValidate) => boolean | string;

export const rules = {
  email(message: string, optional?: boolean): ValidationRule {
    return (value: unknown) => {
      return (isEmpty(value) && optional) || validator.isEmail(value as string) || message;
    };
  },

  phone(message: string, optional?: boolean): ValidationRule {
    return (value: unknown) => {
      return (isEmpty(value) && optional) || /^[0-9()+ -]+$/g.test(value as string) || message;
    };
  },

  hexColor(message: string): ValidationRule {
    return (value: unknown) => {
      return (!isEmpty(value) && validator.isHexColor(value as string)) || message;
    };
  },

  required(message: string): ValidationRule {
    return (value: unknown) => {
      return !isEmpty(value) || message;
    };
  },

  lowerCase(message: string): ValidationRule {
    return (value: unknown) => {
      return checkLowercase(value) || message;
    };
  },

  upperCase(message: string): ValidationRule {
    return (value: unknown) => {
      return checkUppercase(value) || message;
    };
  },

  hasNumber(message: string): ValidationRule {
    return (value: unknown) => {
      return checkHasNumber(value) || message;
    };
  },

  hasNonAlphanumeric(message: string): ValidationRule {
    return (value: unknown) => {
      return checkNonAlphanumeric(value) || message;
    };
  },

  date(message: string, lowLimit?: dayjs.Dayjs): ValidationRule {
    return (value: unknown) => {
      return (
        (!isEmpty(value) &&
          dates.parseDate(value as string, "YYYY-MM-DD").isValid() &&
          (!lowLimit || dates.parseDate(value as string, "YYYY-MM-DD").isAfter(lowLimit))) ||
        message
      );
    };
  },

  numeric(message: string, allowDecimales?: boolean, allowEmpty?: boolean): ValidationRule {
    return (value: unknown) => {
      return (
        (allowEmpty && isEmpty(value)) ||
        (!!value && validator.isNumeric(value as string, { no_symbols: !allowDecimales })) ||
        message
      );
    };
  },

  length(message: string, min: number, max?: number): ValidationRule {
    return (value: unknown) => {
      return (!value && min === 0) || (!!value && validator.isLength(value as string, { min, max })) || message;
    };
  },

  minmax(message: string, min: number | null, max: number | null): ValidationRule {
    return (value: unknown) => {
      const v = Number(value);
      if (isNaN(v)) return "El valor debe ser un número";
      if (min !== null && v < min) return message;
      if (max !== null && v > max) return message;
      return true;
    };
  }
};
